import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { tradePlansApi } from '../../api/tradePlans';
import DWLFLoader from '../../components/DWLFLoader';
import EmptyState from '../../components/common/EmptyState';

const timeframes = [
  { value: '1m', label: '1 Minute' },
  { value: '5m', label: '5 Minutes' },
  { value: '15m', label: '15 Minutes' },
  { value: '30m', label: '30 Minutes' },
  { value: '1h', label: '1 Hour' },
  { value: '4h', label: '4 Hours' },
  { value: '1d', label: 'Daily' },
  { value: '1w', label: 'Weekly' }
];

const TradePlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const plansResponse = await tradePlansApi.listTradePlans();
      
      console.log('Plans response:', plansResponse);
      
      // Handle different response structures
      const extractedPlans = Array.isArray(plansResponse) ? plansResponse : 
                           (plansResponse && Array.isArray(plansResponse.plans)) ? plansResponse.plans : 
                           (plansResponse && plansResponse.data && Array.isArray(plansResponse.data.plans)) ? plansResponse.data.plans : 
                           (plansResponse && Array.isArray(plansResponse.data)) ? plansResponse.data : [];
      
      console.log('Extracted plans:', extractedPlans);
      
      setPlans(extractedPlans);
    } catch (err) {
      setError('Failed to load trade plans');
      console.error('Error loading trade plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlan = async () => {
    try {
      await tradePlansApi.deleteTradePlan(selectedPlan.planId);
      setDeleteDialogOpen(false);
      setSelectedPlan(null);
      loadData();
    } catch (err) {
      setError('Failed to delete trade plan');
      console.error('Error deleting trade plan:', err);
    }
  };

  const PlanCard = ({ plan }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom>
          {plan.name}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" mb={2}>
          {plan.description || 'No description provided'}
        </Typography>

        <Stack direction="row" spacing={1} mb={2}>
          <Chip
            icon={<ScheduleIcon />}
            label={timeframes.find(t => t.value === plan.timeframe)?.label || plan.timeframe}
            size="small"
            variant="outlined"
          />

          {Number(plan.successRate) > 0 && (
            <Chip
              label={`${(plan.successRate * 100).toFixed(1)}% success`}
              size="small"
              color={plan.successRate > 0.6 ? 'success' : plan.successRate > 0.4 ? 'warning' : 'error'}
            />
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary" mb={1}>
          <strong>Entry:</strong> {plan.entryCriteria || 'Not specified'}
        </Typography>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              onClick={() => navigate(`/trade-plans/${plan.planId}/edit`)}
              title="Edit Plan"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedPlan(plan);
                setDeleteDialogOpen(true);
              }}
              title="Delete Plan"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => navigate(`/trade-plans/${plan.planId}/view`)}
              title="View Plan"
            >
              <ViewIcon />
            </IconButton>
          </Box>
          
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/trades/new?planId=${plan.planId}`)}
          >
            Use Plan
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="large" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
        <Box>
          <Typography 
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '1.75rem', md: '2rem' },
              fontWeight: 600,
              letterSpacing: '-0.03em',
              color: 'var(--dwlf-text)',
              mb: 0.5,
            }}
          >
            Trade Plans
          </Typography>
          <Typography 
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.85rem',
              color: 'var(--dwlf-ink-50)',
            }}
          >
            Define your trading strategies and criteria
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/trade-plans/new')}
          sx={{
            fontFamily: '"Space Mono", monospace',
            fontSize: '0.75rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderRadius: 0,
            backgroundColor: 'var(--dwlf-accent)',
            color: 'var(--dwlf-bg)',
            px: 2.5,
            py: 1,
            '&:hover': {
              backgroundColor: 'var(--dwlf-text)',
            },
          }}
        >
          Create Plan
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Typography variant="h6" color="text.secondary">
          My Trade Plans ({plans.length})
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {plans.map((plan) => (
          <Grid size={{ xs: 12 }} key={plan.planId}>
            <Box sx={{ maxWidth: 520 }}>
              <PlanCard plan={plan} />
            </Box>
          </Grid>
        ))}
      </Grid>

      {plans.length === 0 && !loading && (
        <EmptyState
          icon={<AssignmentIcon sx={{ fontSize: 64 }} />}
          title="No trade plans"
          subtitle="Start by creating a plan for a symbol you're watching."
          buttonLabel="Create Plan"
          onAction={() => navigate('/trade-plans/new')}
          showAddIcon
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Trade Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedPlan?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePlan} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TradePlansPage;
